import posthog from 'posthog-js';
import { environment, VERSION } from '../../environments/environment';

const DEFAULT_POSTHOG_HOST = 'https://us.i.posthog.com';
const DEFAULT_POSTHOG_KEY = 'phc_wNKiR7Kd8xQDc6Qno767q8nEvRf8gbpUgBh2jBzRVwYE';
const SENSITIVE_KEY_PATTERN =
  /password|passcode|secret|token|authorization|cookie|email|phone|tel|name|address|signature|base64|image|photo|uri|url/i;
const ANALYTICS_KEY_ALLOWLIST = new Set([
  'image_count',
  'captured_at',
  'gap_from_previous_ms',
  'run_index',
  'capture_ordinal',
]);

const SESSION_RECORDING_CONFIG = {
  sampleRate: 0.1,
  maskAllInputs: true,
  maskInputOptions: {
    password: true,
    email: true,
    tel: true,
    text: true,
    number: true,
  },
  maskTextSelector:
    'input[autocomplete="one-time-code"], input[name*="otp"], input[id*="otp"], [data-ph-mask]',
} as const;

type AnalyticsUser = {
  uid: string;
};

type AnalyticsEvent = {
  name: string;
  data?: Record<string, unknown>;
};

function isSensitiveAnalyticsKey(key: string): boolean {
  return !ANALYTICS_KEY_ALLOWLIST.has(key) && SENSITIVE_KEY_PATTERN.test(key);
}

function sanitizeValue(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map((item) => sanitizeValue(item));
  }

  if (!value || typeof value !== 'object') {
    return value;
  }

  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>).map(([key, nested]) => [
      key,
      isSensitiveAnalyticsKey(key) ? '[Filtered]' : sanitizeValue(nested),
    ]),
  );
}

function getFailureReason(value: unknown): string | undefined {
  if (value === undefined || value === null || value === '') {
    return undefined;
  }

  const message = String(value).toLowerCase();

  if (/permission|denied|unauthori[sz]ed|auth/.test(message)) return 'permission';
  if (/timeout|deadline/.test(message)) return 'timeout';
  if (/network|fetch|offline|connection|internet/.test(message)) return 'network';
  if (/abort|cancel/.test(message)) return 'cancelled';
  if (/base64|decode|encoding/.test(message)) return 'encoding';
  if (/valid|required|missing/.test(message)) return 'validation';
  if (/firebase|firestore|functions/.test(message)) return 'backend';

  return 'unknown';
}

function getAiSuggestionSuccess(data?: Record<string, unknown>): boolean {
  return Boolean(
    data?.accepted_any_suggestion ||
      data?.accepted_defect ||
      data?.accepted_defects ||
      Number(data?.matched_places ?? 0) > 0 ||
      Number(data?.matched_categories ?? 0) > 0 ||
      data?.suggested_category_matches === true
  );
}

function hasAiSuggestions(data?: Record<string, unknown>): boolean {
  return (
    Number(data?.suggestions_defects ?? data?.suggested_defects ?? 0) > 0 ||
    Number(data?.suggestions_places ?? data?.suggested_places ?? 0) > 0 ||
    Number(data?.suggestions_categories ?? data?.suggested_categories ?? 0) > 0
  );
}

function normalizeAnalyticsProperties(data?: Record<string, unknown>): Record<string, unknown> | undefined {
  if (!data) {
    return undefined;
  }

  const normalized: Record<string, unknown> = { ...data };
  normalized.duration_ms ??= normalized.duration ?? normalized.durationMs ?? normalized.uploadDuration;
  normalized.file_size_bytes ??= normalized.fileSizeBytes ?? normalized.imageSize;
  normalized.blocks_count ??= normalized.blocksCount;
  normalized.report_type ??= normalized.reportType;
  normalized.failure_reason ??= normalized.error ?? normalized.errorMessage;
  normalized.failure_reason = getFailureReason(normalized.failure_reason);
  normalized.failure_code ??= normalized.errorCode ?? normalized.status_code ?? normalized.statusCode;
  delete normalized.error;
  delete normalized.errorMessage;

  return Object.fromEntries(Object.entries(normalized).filter(([, value]) => value !== undefined));
}

function getDerivedAnalyticsEvents(name: string, data?: Record<string, unknown>): AnalyticsEvent[] {
  const normalized = normalizeAnalyticsProperties(data);
  const derived: AnalyticsEvent[] = [];

  if (name === 'image_upload_success') {
    derived.push({ name: 'image_upload_completed', data: { ...normalized, success: true } });
  }
  if (name === 'image_upload_error') {
    derived.push({ name: 'image_upload_failed', data: { ...normalized, success: false } });
  }
  if (name === 'create_report') {
    derived.push({ name: 'report_created', data: normalized });
  }
  if (name === 'report_published') {
    derived.push({ name: 'report_publish_completed', data: { ...normalized, success: true } });
  }
  if (name === 'ai_suggestion_click') {
    derived.push({ name: 'ai_suggestion_selected', data: normalized });
  }
  if (name === 'ai_suggestion_outcome') {
    if (hasAiSuggestions(normalized)) {
      derived.push({
        name: 'ai_suggestion_evaluated',
        data: {
          ...normalized,
          has_suggestions: true,
          accepted_any_suggestion: getAiSuggestionSuccess(normalized),
          success: getAiSuggestionSuccess(normalized),
        },
      });
    }
  }

  return derived;
}

export function initAnalytics(): void {
  // Init runs before bootstrapApplication (outside NgZone). Keep sampleRate low and
  // canvas recording off so rrweb mutations do not worsen Zone.js upload jank.
  posthog.init(DEFAULT_POSTHOG_KEY, {
    api_host: DEFAULT_POSTHOG_HOST,
    autocapture: false,
    capture_pageview: 'history_change',
    capture_pageleave: true,
    disable_session_recording: false,
    session_recording: { ...SESSION_RECORDING_CONFIG },
    loaded: (client) => {
      if (!environment.production) {
        client.debug();
      }

      client.capture('analytics_initialized', {
        app: 'angular-app',
        environment: environment.production ? 'production' : 'development',
        release: VERSION,
      });
    },
  });

  posthog.register({
    app: 'angular-app',
    environment: environment.production ? 'production' : 'development',
    release: VERSION,
  });
}

export function setAnalyticsUser(user: AnalyticsUser): void {
  posthog.identify(user.uid);
}

export function resetAnalyticsUser(): void {
  setTimeout(() => posthog.reset(), 1000);
}

export function trackAnalyticsEvent(name: string, data?: Record<string, unknown>): void {
  const safeData = sanitizeValue(normalizeAnalyticsProperties(data)) as Record<string, unknown> | undefined;

  posthog.capture(name, safeData);

  for (const event of getDerivedAnalyticsEvents(name, safeData)) {
    posthog.capture(event.name, event.data);
  }
}
